import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux"
import { setUserData } from "../Module/Shared/SharedSlices/UserDataSlice"
import { hasAuthenticationToken } from "../Module/Authentication/SignIn/SigninSlice"
import Routing from "./Routing/Routing"
import StorageService from "./StorageService"
import { userNotificationGetRequest } from "../Module/Notifications/UserNotificationServiceRequest"
import { setUserNotifications } from "../Module/Notifications/NotificationSlice"
import { RootState } from "./store"
import { RoleType, StorageKey } from "./enums"
import { Role } from "../Types/RoleType"
import { organizationNotificationGetRequest } from "../Module/Notifications/OrganizationNotificationServiceRequest"
import SessionService from "./SessionService"
import { chatListGetRequest } from "../Module/Chat/ChatServiceRequest"
import { incrementUnseenMessagesCount, setConversationList } from "../Module/Chat/ChatSlice"
import { ChakraProvider, Toast, createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import React from "react";
import { AppSocketProvider, useAppSocket } from "../Module/Shared/Socket/AppSocketProvider";
import { StatusBarConfig } from "./Helpers/StatusBarConfig";
import { KeyboardConfig } from "./Helpers/KeyboardConfig";
import { Toaster, toaster } from "./Helpers/ToastService";
import { pushNotificationsManager, PushNotificationActionData } from "./Helpers/PushNotificationsManager";
import { appIconBadgeService } from "./Helpers/AppIconBadgeService";
import { Capacitor } from "@capacitor/core";

function normalizePushRedirectPath(redirectUrl: string): string {
    try {
        const url = new URL(redirectUrl)
        const normalizedPath = `${url.pathname}${url.search}${url.hash}`
        return normalizedPath || "/"
    } catch (_error) {
        if (redirectUrl.startsWith("/")) {
            return redirectUrl
        }
        return `/${redirectUrl}`
    }
}

function resolvePushNavigationPath(
    redirectUrl: string,
    role: Role,
    organizationId: string | null,
    data?: PushNotificationActionData
): string {
    const normalizedPath = normalizePushRedirectPath(redirectUrl)
    const notificationType = data?.notification_type || ""
    const roomId = data?.room_id ? String(data.room_id) : null
    const postId = data?.post_id ? String(data.post_id) : null
    const invitationId = data?.invitation_id ? String(data.invitation_id) : null
    const eventCreatorId = data?.event_creator_id ? String(data.event_creator_id) : null
    const conversationId = data?.conversation_id ? String(data.conversation_id) : null

    const isOrganizationRole = role === RoleType.ORGANIZATION && Boolean(organizationId)

    if (notificationType === "event_comment" && invitationId && eventCreatorId) {
        return `/organization-profile/${eventCreatorId}/event/${invitationId}`
    }

    if (["new_post_in_room", "comment", "commented_on_same_post"].includes(notificationType) && roomId && postId) {
        if (isOrganizationRole) {
            return `/room-post/${organizationId}/${roomId}/${postId}`
        }
        return `/room-post/${roomId}/${postId}`
    }

    if (["room_invitation", "org_room_invitation", "room_invitation_reply", "room_join_request_reply", "new_member", "org_room_join_request"].includes(notificationType) && roomId) {
        if (isOrganizationRole) {
            return `/room/${organizationId}/${roomId}`
        }
        return `/room/${roomId}`
    }

    if (["room_join_request"].includes(notificationType)) {
        if (isOrganizationRole) {
            return `/invitations/${organizationId}`
        }
        return "/invitations"
    }

    if (["chat_message"].includes(notificationType)) {
        if (conversationId) {
            return `/chat?conversation=${conversationId}`
        }
        return "/chat"
    }

    if (!isOrganizationRole) {
        return normalizedPath
    }

    if (/^\/notifications\/?$/.test(normalizedPath)) {
        return `/notifications/${organizationId}`
    }
    if (/^\/FAQ\/?$/.test(normalizedPath)) {
        return `/FAQ/${organizationId}`
    }
    if (/^\/newest-members\/?$/.test(normalizedPath)) {
        return `/newest-members/${organizationId}`
    }
    if (/^\/newest-organizations\/?$/.test(normalizedPath)) {
        return `/newest-organizations/${organizationId}`
    }
    if (/^\/search-screen\/?$/.test(normalizedPath)) {
        return `/search-screen/${organizationId}`
    }
    if (/^\/invitations\/?$/.test(normalizedPath)) {
        return `/invitations/${organizationId}`
    }
    if (/^\/upcoming-events\/?$/.test(normalizedPath)) {
        return `/upcoming-events/${organizationId}`
    }

    const invalidOrgProfilePathMatch = normalizedPath.match(/^\/organization-profile\/(\d+)\/(\d+)\/?$/)
    if (invalidOrgProfilePathMatch) {
        return `/organization-profile/${invalidOrgProfilePathMatch[1]}`
    }

    const roomPathMatch = normalizedPath.match(/^\/room\/(\d+)\/?$/)
    if (roomPathMatch) {
        return `/room/${organizationId}/${roomPathMatch[1]}`
    }

    const roomPostPathMatch = normalizedPath.match(/^\/room-post\/(\d+)\/(\d+)\/?$/)
    if (roomPostPathMatch) {
        return `/room-post/${organizationId}/${roomPostPathMatch[1]}/${roomPostPathMatch[2]}`
    }

    return normalizedPath
}

function SocketUnseenMessagesBridge(props: { isAuthenticated: boolean; role: Role; userId: number }) {
    const dispatch = useDispatch()
    const { addMessageListener } = useAppSocket()

    useEffect(() => {
        if (!props.isAuthenticated || props.role !== RoleType.USER) {
            return
        }

        const unsubscribe = addMessageListener((message) => {
            if (message.msg_type !== "new.message") {
                return
            }

            const currentPath = window.location.pathname || ""
            const isInChatScreen = currentPath.includes("/chat")
            const senderId = message?.data?.sender?.id
            const isOwnMessage = typeof senderId === "number" && senderId === props.userId

            if (!isOwnMessage && !isInChatScreen) {
                dispatch(incrementUnseenMessagesCount(1))
            }
        })

        return () => {
            unsubscribe()
        }
    }, [addMessageListener, dispatch, props.isAuthenticated, props.role, props.userId])

    return null
}


function App() {
    const dispatch = useDispatch()
    const MINUTE_MS = 30000
    const organiztionId = SessionService.getItem(StorageKey.organizationId)
    const localUserData = StorageService.parse(StorageService.getItem("user") as string)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const activeConversationId = useSelector((state: RootState) => state.chatSlice.activeConversationId)
    const unseenMessagesCount = useSelector((state: RootState) => state.chatSlice.unseenMessagesCount)
    const unreadNotificationsCount = useSelector((state: RootState) =>
        state.UserNotificationSlice.notifications.filter((notification) => notification.read === false).length
    )
    const isAuthenticated = useSelector((state: RootState) => state.signinSlice.authenticationSlice.hasAccessToken)
    const accessToken = StorageService.getItem(StorageKey.accessToken)
    const isNativePlatform = Capacitor.isNativePlatform()
    const nativePlatform = isNativePlatform ? Capacitor.getPlatform() : "web"
    dispatch<any>(hasAuthenticationToken({ hasAccessToken: StorageService.hasItem("accessToken") }))

    const refreshUnreadCounters = useCallback(() => {
        if (!isAuthenticated) {
            return
        }

        if (role === RoleType.USER) {
            userNotificationGetRequest().then(response => {
                if (response.status === 200) {
                    dispatch<any>(setUserNotifications(response.data))
                }
            })

            chatListGetRequest().then(response => {
                if (response.status === 200) {
                    dispatch<any>(setConversationList(response.data.results))
                }
            })
            return
        }

        if (role === RoleType.ORGANIZATION && organiztionId) {
            organizationNotificationGetRequest(organiztionId).then(response => {
                if (response.status === 200) {
                    dispatch<any>(setUserNotifications(response.data))
                }
            })
        }
    }, [dispatch, isAuthenticated, organiztionId, role])

    // Initialize native app features and only register push token once auth is available.
    useEffect(() => {
        const initializeStatusBar = async () => {
            await StatusBarConfig.initialize()
            await appIconBadgeService.initialize()
            await KeyboardConfig.initialize()
        }

        initializeStatusBar()
    }, [])

    useEffect(() => {
        const initializePushListeners = async () => {
            await pushNotificationsManager.initialize((redirectUrl: string, data?: PushNotificationActionData) => {
                const redirectPath = resolvePushNavigationPath(redirectUrl, role, organiztionId || null, data)
                window.location.assign(redirectPath)
            }, (data?: PushNotificationActionData) => {
                if (!isAuthenticated) {
                    return
                }

                const notificationType = data?.notification_type || ""
                if (notificationType === "chat_message") {
                    const rawSenderId = data?.sender_id
                    const incomingSenderId =
                        typeof rawSenderId === "number"
                            ? rawSenderId
                            : Number(rawSenderId)

                    const rawConversationId = data?.conversation_id
                    const incomingConversationId =
                        typeof rawConversationId === "number"
                            ? rawConversationId
                            : Number(rawConversationId)

                    const isOwnMessage = Number.isInteger(incomingSenderId) && incomingSenderId === localUserData?.id
                    const isCurrentlyOpenConversation =
                        Number.isInteger(incomingConversationId) &&
                        activeConversationId > 0 &&
                        incomingConversationId === activeConversationId

                    if (!isOwnMessage && !isCurrentlyOpenConversation) {
                        dispatch(incrementUnseenMessagesCount(1))
                    }
                }

                if (role === RoleType.USER) {
                    userNotificationGetRequest().then(response => {
                        if (response.status === 200) {
                            dispatch<any>(setUserNotifications(response.data))
                        }
                    })
                    return
                }

                if (role === RoleType.ORGANIZATION && organiztionId) {
                    organizationNotificationGetRequest(organiztionId).then(response => {
                        if (response.status === 200) {
                            dispatch<any>(setUserNotifications(response.data))
                        }
                    })
                }
            }, (data?: PushNotificationActionData) => {
                const notificationType = data?.notification_type || ""
                if (notificationType !== "chat_message") {
                    return false
                }

                const currentPath = window.location.pathname || ""
                const isUserInChatScreen = /^\/chat\/?$/.test(currentPath)
                if (!isUserInChatScreen) {
                    return false
                }

                const rawConversationId = data?.conversation_id
                const incomingConversationId =
                    typeof rawConversationId === "number"
                        ? rawConversationId
                        : Number(rawConversationId)

                if (!Number.isInteger(incomingConversationId)) {
                    return false
                }

                return activeConversationId > 0 && incomingConversationId === activeConversationId
            })
        }

        initializePushListeners()
    }, [activeConversationId, dispatch, isAuthenticated, localUserData?.id, organiztionId, role])

    useEffect(() => {
        if (!isAuthenticated) {
            return
        }

        const syncPushRegistration = async () => {
            await pushNotificationsManager.syncRegistration()
        }

        syncPushRegistration()
    }, [isAuthenticated])

    useEffect(() => {
        StorageService.hasItem("user")
            ? dispatch<any>(
                setUserData({
                    id: localUserData.id,
                    email: localUserData.email,
                    location: localUserData.location,
                    cover_photo: localUserData.cover_photo,
                    first_name: localUserData.first_name,
                    last_name: localUserData.last_name,
                    description: localUserData.description,
                    edusiia_reasons: localUserData.edusiia_reasons,
                    profile_photo: localUserData.profile_photo,
                    privacy_settings: localUserData.privacy_settings,
                    hashtags: localUserData.hashtags,
                    online: localUserData.online,
                    tutorial_progress: localUserData.tutorial_progress
                })
            )
            : dispatch<any>(
                setUserData({
                    id: 0,
                    email: "",
                    location: "",
                    cover_photo: { id: 0, url_path: "" },
                    first_name: "",
                    last_name: "",
                    description: "",
                    edusiia_reasons: "",
                    profile_photo: { id: 0, url_path: "" },
                    hashtags: [],
                    online: false,
                    tutorial_progress: 0
                })
            )
    }, [
        dispatch,
        localUserData?.cover_photo,
        localUserData?.description,
        localUserData?.edusiia_reasons,
        localUserData?.email,
        localUserData?.first_name,
        localUserData?.hashtags,
        localUserData?.id,
        localUserData?.last_name,
        localUserData?.location,
        localUserData?.privacy_settings,
        localUserData?.profile_photo,
        localUserData?.online,
        localUserData?.tutorial_progress,
    ])

    useEffect(() => {
        if (isAuthenticated && role === RoleType.USER) {
            chatListGetRequest().then(response => {
                if (response.status === 200) {
                    dispatch<any>(setConversationList(response.data.results))
                }
            })
        }
    }, [dispatch, isAuthenticated, role])

    useEffect(() => {
        if (!isAuthenticated) {
            appIconBadgeService.setCount(0)
            return
        }

        const totalUnreadCount = unreadNotificationsCount + unseenMessagesCount
        appIconBadgeService.setCount(totalUnreadCount)
    }, [isAuthenticated, unreadNotificationsCount, unseenMessagesCount])

    useEffect(() => {
        if (!isAuthenticated) {
            return
        }

        const refreshOnForeground = () => {
            if (document.visibilityState === "visible") {
                refreshUnreadCounters()
            }
        }

        const refreshOnWindowFocus = () => {
            refreshUnreadCounters()
        }

        document.addEventListener("visibilitychange", refreshOnForeground)
        window.addEventListener("focus", refreshOnWindowFocus)

        return () => {
            document.removeEventListener("visibilitychange", refreshOnForeground)
            window.removeEventListener("focus", refreshOnWindowFocus)
        }
    }, [isAuthenticated, refreshUnreadCounters])

    useEffect(() => {
        if (isAuthenticated && role === RoleType.USER) {
            const interval = setInterval(() => {
                userNotificationGetRequest().then(response => {
                    if (response.status === 200) {
                        dispatch<any>(setUserNotifications(response.data))
                    }
                })
            }, MINUTE_MS)
            return () => clearInterval(interval)
        }
        if (isAuthenticated && role === RoleType.ORGANIZATION) {
            const interval = setInterval(() => {
                organizationNotificationGetRequest(organiztionId!).then(response => {
                    if (response.status === 200) {
                        dispatch<any>(setUserNotifications(response.data))
                    }
                })
            }, MINUTE_MS)
            return () => clearInterval(interval)
        }
    }, [organiztionId, dispatch, isAuthenticated, role])

    const config = defineConfig({
        theme: {
            tokens: {
                // your custom tokens here, or leave empty to use defaults
            }
        }
        // you can also configure preflight, globalCss, etc.
    });

    const cache = createCache({ key: "chakra", prepend: true });

    const system = createSystem(defaultConfig, config);

    return (
        <CacheProvider value={cache}>
            <ChakraProvider value={system}>
                <Toaster toaster={toaster}>
                    {(toast) => (
                        <Toast.Root
                            className={isNativePlatform ? `native-push-toast native-push-toast--${nativePlatform}` : undefined}
                            width="fit-content"
                            maxWidth="min(90vw, 32rem)"
                            minHeight="unset"
                            borderRadius="md"
                            px="4"
                            py="3"
                            mx="auto"
                            cursor={toast.action ? "pointer" : "default"}
                            onClick={() => {
                                if (toast.action?.onClick) {
                                    toast.action.onClick()
                                }
                            }}
                        >
                            <Toast.Title>{toast.title}</Toast.Title>
                            {toast.description ? <Toast.Description>{toast.description}</Toast.Description> : null}
                            <Toast.CloseTrigger onClick={(event) => event.stopPropagation()} />
                        </Toast.Root>
                    )}
                </Toaster>
                <AppSocketProvider accessToken={accessToken} shouldConnect={Boolean(isAuthenticated)}>
                    <SocketUnseenMessagesBridge
                        isAuthenticated={Boolean(isAuthenticated)}
                        role={role}
                        userId={localUserData?.id || 0}
                    />
                    <Routing />
                </AppSocketProvider>
            </ChakraProvider>
        </CacheProvider>

    )
}

export default App